// Teacher's Corner — deck data
// Each deck: slides[]. Each slide: { title, subtitle?, items[] }.
// items[] reveal one-at-a-time on click / arrow / space.
// This is the FULL app content, not an overview. Every section is broken
// out so a presenter can walk an audience through it slide-by-slide.

// ────────────────────────────────────────────────────────────────────────
// DECK 1 — THE BODY
// ────────────────────────────────────────────────────────────────────────
const D_BODY = {
  id: 'the-body',
  title: 'The Body',
  tagline: 'FlowGrid · Control Pause · The Reset · 14 Drills',
  color: '#C45A3B',
  slides: [
    {
      title: 'The Body',
      subtitle: 'The first hub — physical architecture',
      items: [
        'Everything the app teaches is built on a body that can hold what you\'re about to say.',
        'This hub has four tools: FlowGrid, Control Pause, The Reset, and the Drills library.',
        'Nothing here is about performance. It\'s about being able to show up on days you\'d rather not.',
      ],
    },
    {
      title: 'FlowGrid',
      subtitle: 'Physical Workouts — randomized, unfakeable',
      items: [
        'The app picks the tool for you at random. You don\'t get to game the workout.',
        'Long-bar movements can\'t repeat back-to-back — the grid enforces variety.',
        'Between rounds you land on a wait screen with a rotating icon so the body has a beat to reset.',
        'You can save FlowGrid preferences — which equipment you own — so it never asks for a tool you don\'t have.',
        'The point isn\'t performance. The point is showing up.',
      ],
    },
    {
      title: 'Control Pause',
      subtitle: 'One number, honestly measured',
      items: [
        'Sit down. Breathe in and out normally. Pinch your nose after the exhale.',
        'Start the timer. Stop the moment you feel the first real urge to breathe.',
        'The number that comes back is your CP — a proxy for CO₂ tolerance and nervous-system calm.',
        'Auto-pause: if your phone locks or a call comes in, the timer stops so the number stays honest.',
        'Track it. Watch it move. That\'s the whole practice.',
      ],
    },
    {
      title: 'The Reset',
      subtitle: '90 seconds before you say the hard thing',
      items: [
        'A short, guided nervous-system reset — shoulders, chin, breath.',
        'Run it before any conversation you don\'t want to have.',
        'It\'s wired into Operation Listen too — Coach and Guide are prompted to run it before the round starts.',
        'Quick version and full version — pick based on how much time you actually have.',
      ],
    },
    {
      title: 'Drills — the library',
      subtitle: '14 short physical practices',
      items: [
        'Posture, breath, gaze, hands, chin — every drill isolates one channel of body language.',
        'Each drill is 2–3 steps long. Say a line one way, then the other way, notice the difference.',
        'You can revisit any drill any time — the drill remembers the last time you did it.',
        'Flashcards nudge you towards drills based on what came up in your last session.',
      ],
    },
    {
      title: 'The 14 Drills — by channel',
      items: [
        'Face — Face Dial (think a word, don\'t move a muscle).',
        'Eyes — Eye Anchor (steady gaze vs darting).',
        'Chin — Chin Check (down / level / up).',
        'Head — Still Head, Moving Mind (imagined nod).',
        'Hands — Hand Grip Dial (fingertips / palm / wrist).',
        'Arms — The Conductor\'s Arms (staccato vs flowing).',
        'Feet — Root & Rise (light-footed vs rooted).',
        'Spine — Daily Posture Audit (eight-point mirror check).',
        'Voice — Status Dial · Listen Softer · Good Food–Good Chat.',
        'Mind — Reappraise Pause · One More Thing · AAA Checklist.',
      ],
    },
  ],
};

// ────────────────────────────────────────────────────────────────────────
// DECK 2 — THE BODY: DRILLS IN DETAIL
// ────────────────────────────────────────────────────────────────────────
const D_BODY_DRILLS = {
  id: 'body-drills',
  title: 'Body Drills — in detail',
  tagline: 'Every drill · Every step · Every reflection',
  color: '#C45A3B',
  slides: [
    {
      title: 'Eye Anchor',
      subtitle: '"Where your eyes go, your ears follow."',
      items: [
        'Step 1 — Pick one fixed point across the room. Hold a slow, steady gaze on it while you say: "I hear you, and I\'m not going anywhere."',
        'Step 2 — Say the same line while letting your eyes dart around the room.',
        'Reflect — Which version sounded more grounded? Which one would you trust more?',
        'Carry — A still gaze reads as steady before a word is said.',
      ],
    },
    {
      title: 'Chin Check',
      subtitle: 'Down / Level / Up',
      items: [
        'Step 1 — Drop your chin slightly. Say: "Let\'s take a closer look at this."',
        'Step 2 — Level chin. Say it again.',
        'Step 3 — Raise your chin slightly. Say it again.',
        'Reflect — Which version sounded most like the authority you meant to bring?',
        'Carry — Where do you live by default — down, level, or up?',
      ],
    },
    {
      title: 'Face Dial',
      subtitle: 'Think the expression. Skip the muscle.',
      items: [
        'Step 1 — Relax your face completely. Think the word "smile." Count to ten.',
        'Step 2 — Think the word "frown." Count to ten.',
        'Step 3 — Think the word "neutral." Count to ten.',
        'Reflect — Which one changed your pitch the most, without you moving a muscle?',
        'Carry — Less is more. Let your thinking guide the tone.',
      ],
    },
    {
      title: 'Still Head, Moving Mind',
      subtitle: 'Full-body yes or no',
      items: [
        'Step 1 — Keep your head completely still. Imagine nodding yes. Count to ten.',
        'Step 2 — Still head. Imagine shaking no. Count to ten.',
        'Step 3 — Still head. Imagine tilting side to side. Count to ten.',
        'Reflect — Did your head want to move to match your thinking? What did that do to your tone?',
        'Carry — Better to get — and give — a full body yes or no.',
      ],
    },
    {
      title: 'Hand Grip Dial',
      subtitle: 'Fingertips / Palm / Wrist',
      items: [
        'Step 1 — Hold any nearby object with just your fingertips. Say: "I\'d love to hear more about that."',
        'Step 2 — Hold it with your full palm. Say it again.',
        'Step 3 — Hold it with wrist awareness — light, present. Say it again.',
        'Reflect — Which grip matched the tone you actually wanted to bring?',
        'Carry — Fingertips explore. Palm asserts. Wrist stays graceful.',
      ],
    },
    {
      title: "The Conductor's Arms",
      subtitle: 'Staccato vs flowing',
      items: [
        'Step 1 — Move your arms in sharp, straight lines — up, down, away. Count to ten.',
        'Step 2 — Move your arms in slow, sweeping arcs. Count to ten.',
        'Reflect — When would staccato serve you better than flow — and when would flow?',
        'Carry — You\'re the conductor. Choose the shape on purpose.',
      ],
    },
    {
      title: 'Root & Rise',
      subtitle: 'Light-footed vs rooted',
      items: [
        'Step 1 — Stand loose. Let your feet feel light, almost ready to lift off. Count out loud to ten.',
        'Step 2 — Plant your feet like they\'re rooted to the ground. Count to ten again.',
        'Reflect — What changed in your tone between light-footed and rooted?',
        'Carry — Next time you need to hold a line, put your foot down.',
      ],
    },
    {
      title: 'Daily Posture Audit',
      subtitle: 'Eight zones · 20-second mirror check',
      items: [
        'Shoulders relaxed, not raised toward your ears.',
        'Chin level, not tipped up or down.',
        'Weight even across both feet.',
        'Jaw unclenched.',
        'Lower spine grounded, not bracing.',
        'Mid spine lets your breath move freely.',
        'Upper spine supporting your voice, not collapsing it.',
        'Neck and head steady, not scattered.',
      ],
    },
    {
      title: 'Status Dial',
      subtitle: 'High / Level / Low',
      items: [
        'Step 1 — Say this line aiming for high status: "We need to talk about the deadline."',
        'Step 2 — Say it again aiming level, as an equal.',
        'Step 3 — Say it once more aiming low status, deferential.',
        'Reflect — Which lands closest to calm-and-open? Which lands closest to clipped-and-closed? Is your default the one this conversation actually needs?',
        'Carry — You don\'t have to live permanently in one register.',
      ],
    },
    {
      title: 'Listen Softer',
      subtitle: 'Softer listening → softer voice',
      items: [
        'Step 1 — Sit quietly for 20 seconds. Try to catch the faintest sound in the room — even an imagined pin drop.',
        'Step 2 — Speak this line at your normal volume: "Can we talk for a minute?"',
        'Step 3 — Say it again, aiming softer — not quieter effort, just softer listening.',
        'Reflect — What did you notice about your volume the second time?',
        'Carry — Listen softer. The volume tends to follow.',
      ],
    },
    {
      title: 'Good Food — Good Chat',
      subtitle: 'Taste changes tone',
      items: [
        'Step 1 — Describe your day out loud as if it\'s the most delicious meal you\'ve ever had.',
        'Step 2 — Describe it again as if it\'s disgusting.',
        'Reflect — How did your word choice — not just your tone — change between the two?',
        'Carry — Taste changes tone, and tone changes word choice.',
      ],
    },
    {
      title: 'One More Thing',
      subtitle: 'Suspicion → Curiosity',
      items: [
        'Step 1 — Ask out loud, imagining suspicion, tongue tip lifted: "Where were you?"',
        'Step 2 — Ask it again with open curiosity, tongue relaxed.',
        'Reflect — Which version actually invites someone to answer?',
        'Carry — Reduce suspicion. Increase inquiry.',
      ],
    },
    {
      title: 'Reappraise Pause',
      subtitle: 'Do I really mean that? Do they really mean that?',
      items: [
        'Prompt 1 — What\'s on your mind right now?',
        'Prompt 2 — Before you react — do I really mean that?',
        'Prompt 3 — Before you judge their side — do they really mean that?',
        'Carry — Paused and reappraised beats reacted and regretted.',
      ],
    },
  ],
};

// ────────────────────────────────────────────────────────────────────────
// DECK 3 — THE VOICE (overview)
// ────────────────────────────────────────────────────────────────────────
const D_VOICE = {
  id: 'the-voice',
  title: 'The Voice',
  tagline: 'Tonal Influence · Listening Chain · AAA · Operation Listen',
  color: '#C45A3B',
  slides: [
    {
      title: 'The Voice',
      subtitle: 'Second hub — what happens in the room',
      items: [
        'Tonal Influence — the audiobook. Listen on your own.',
        'The Listening Chain — the five-gate framework for hard conversations.',
        'AAA Checklist — Agency, Autonomy, Accountability. A private self-check.',
        'Operation Listen — the live drill that puts it all under pressure.',
      ],
    },
    {
      title: 'Why this hub exists',
      subtitle: 'Reading about it doesn\'t rewire it',
      items: [
        'Voice work is what you do in a room, in real time, with a real person.',
        'The theory only sticks when it\'s been tested under pressure.',
        'Operation Listen is the pressure. The rest of this hub is what gets tested there.',
      ],
    },
    {
      title: 'How the four tools relate',
      items: [
        'Tonal Influence gives you the language.',
        'The Listening Chain gives you the structure.',
        'AAA gives you the self-check before you enter the room.',
        'Operation Listen is the practice arena where all three get exercised.',
      ],
    },
  ],
};

// ────────────────────────────────────────────────────────────────────────
// DECK 4 — THE LISTENING CHAIN (full detail — every gate)
// ────────────────────────────────────────────────────────────────────────
const D_LISTENING_CHAIN = {
  id: 'listening-chain',
  title: 'The Listening Chain',
  tagline: 'One posture + five gates + seven rungs + dignity statements',
  color: '#C45A3B',
  slides: [
    {
      title: 'The Chain',
      subtitle: 'Before → Voice → Time → Story → Respect → Next Steps',
      items: [
        'One posture at the start: Be willing to be wrong.',
        'Five gates in order: Voice · Time · The Empathy Ladder · Respect · Next Steps.',
        'Inside The Empathy Ladder, seven rungs.',
        'Inside Respect, five dignity moves.',
        'Skip a gate and the ones after it don\'t open cleanly.',
      ],
    },

    // ── BEFORE WE BEGIN ──
    {
      title: 'Before We Begin',
      subtitle: 'Not a gate. A posture.',
      items: [
        'The posture: "I may be wrong about the time, the place, the tool, the person…"',
        'This comes before Gate 1 — it\'s what makes all five gates easier to open.',
        'People correct you gently when you\'re soft. They argue when you\'re certain.',
      ],
    },
    {
      title: 'Before We Begin — the moves',
      subtitle: 'What willingness-to-be-wrong looks like',
      items: [
        'The move — Say it at the start: "I may be wrong about the time, the place, the tool, the person…" It changes the whole atmosphere.',
        'Why it works — When you open with the possibility of being wrong, they don\'t need to prove it. Defence drops. Correction replaces argument.',
        'What it isn\'t — Not self-doubt. Not apology. A clear position, held loosely. That combination is disarming.',
        'When it breaks — Certainty at the start closes the room. Their only move is to push back.',
        'Carry — Start soft. The gates open easier.',
      ],
    },

    // ── GATE 1 VOICE ──
    {
      title: 'Gate 1 — Voice',
      subtitle: 'Do they have a say?',
      items: [
        'Both people need permission to say the real thing.',
        'Without it, the conversation happens — but it\'s edited.',
      ],
    },
    {
      title: 'Voice — the three checks',
      items: [
        'They can speak — Have you actually made space for them, or just for your version of them?',
        'You have a say too — Are you saying what\'s true, or what\'s easier to hear?',
        'Watch your signals — Your face, your rank, your silence — all of it tells them whether honesty is safe.',
        'When it breaks — The conversation happens. The real content doesn\'t.',
        'Carry — Both people get a real say.',
      ],
    },

    // ── GATE 2 TIME ──
    {
      title: 'Gate 2 — Time',
      subtitle: 'Is now the right time?',
      items: [
        'Say what time you have before they start — or wait until you have real time to give.',
      ],
    },
    {
      title: 'Time — the four moves',
      items: [
        'Minutes available — "I have ten minutes." Said before they begin, not after.',
        'Wait until later — A short conversation with full attention beats a long one where you\'re somewhere else.',
        'Name what you\'re bringing — If it\'s a big conversation, say so. Don\'t let it surprise them.',
        'Be here or reschedule — Partial presence is worse than absence. It tells them they\'re low priority.',
        'When it breaks — Voice was given. Time wasn\'t. They finished talking and still weren\'t heard.',
        'Carry — Give real time — or schedule it.',
      ],
    },

    // ── GATE 3 STORY & EMPATHY ──
    {
      title: 'Gate 3 — The Empathy Ladder',
      subtitle: 'Gather before you react',
      items: [
        'Before you redirect or respond — find out what\'s actually happening.',
        'Seven rungs. Two paths (Self and Other).',
        'The rung you\'re on tells you what you\'re actually working with.',
      ],
    },
    {
      title: 'The Empathy Ladder — the ground rules',
      items: [
        'Use before redirecting — The rung you\'re on tells you which gate to address. Diagnose first, then redirect.',
        'Both paths matter — Self reveals what you\'re carrying in. Other reveals what they\'re carrying. Both affect what gets said and what gets heard.',
        'Accurate information changes the move — A redirect that lands is built on what\'s actually happening — not what you assumed.',
        'When it breaks — Voice opened, Time was given, but neither person checked what they were carrying.',
        'Carry — Gather before you react. Then redirect.',
      ],
    },
    {
      title: 'The Empathy Ladder · SELF',
      subtitle: 'What am I carrying into this?',
      items: [
        '1. I Feel _______ — Name it without defending it. The feeling is information, not a verdict.',
        '2. One Off, or? — Is this actually new, or are you carrying something older into this moment?',
        '3. Old / New? — Is the hurt from right now, or did this trigger something that already lived in you?',
        '4. On Purpose? — Did you mean to cause harm, or did impact outrun your intention?',
        '5. Who\'s Fault? — Fault is a dead end. What\'s your part in how this got here?',
        '6. Proof? — What are you actually certain of — and what are you filling in yourself?',
        '7. Ask / Demand? — Are you making a request or issuing a requirement? Only one invites a yes.',
      ],
    },
    {
      title: 'Self Rungs — the redirect on each',
      subtitle: 'What to ask yourself',
      items: [
        '1 Feel — What is this feeling actually telling you about this moment, right now?',
        '2 One Off — If this were the only time, would it still feel this large?',
        '3 Old/New — What would this feel like if it had no history behind it?',
        '4 On Purpose — What did you actually intend — and is that what landed?',
        '5 Fault — What could you have done differently — not to take the blame, but to find your handle?',
        '6 Proof — Separate what you observed from what you interpreted. What\'s left when you do?',
        '7 Ask/Demand — Can you say what you need in a way that leaves them room to respond?',
      ],
    },
    {
      title: 'The Empathy Ladder · OTHER',
      subtitle: 'What is this person carrying?',
      items: [
        '1. I Feel _______ — Under their complaint or charge is a feeling. That\'s what needs to be heard first.',
        '2. One Off, or? — First time, or the weight of a pattern? The answer changes how you receive it.',
        '3. Old / New? — What they\'re bringing may be carrying older weight — from before you.',
        '4. On Purpose? — They may believe the harm was intentional. That belief shapes everything.',
        '5. Who\'s Fault? — They may have assigned fault already. Understand what they need that assignment to do.',
        '6. Proof? — What they\'re certain of may be interpreted or assumed — and they may not know it yet.',
        '7. Ask / Demand? — Real ask, or ultimatum from a place of pain? Find the real need under the demand.',
      ],
    },
    {
      title: 'Other Rungs — the redirect on each',
      subtitle: 'How to receive it',
      items: [
        '1 Feel — Make room for what they\'re feeling before you respond to what they said.',
        '2 One Off — Is this new information, or confirmation of something already building?',
        '3 Old/New — The older layer may need naming before the present one can move.',
        '4 On Purpose — Don\'t defend against the assumption. Understand what it would feel like to believe it.',
        '5 Fault — What does it cost them to let go of the fault line? That tells you what they need.',
        '6 Proof — Let them say it fully before you address what might be missing. Premature correction sounds like dismissal.',
        '7 Ask/Demand — Find the actual ask under the demand. The demand softens when the real need gets heard.',
      ],
    },

    // ── GATE 4 RESPECT (DIGNITY STATEMENTS - user's example) ──
    {
      title: 'Gate 4 — Respect',
      subtitle: 'Does their dignity stay intact?',
      items: [
        'Dignity is the first thing at risk in conflict.',
        'Keep it in the room — for them and for yourself.',
        'Five categories of Dignity Statements — each one a category of moves you can make.',
        'When it breaks — The story was heard. The person felt processed, not seen.',
        'Carry — See the person. Not just the problem.',
      ],
    },
    {
      title: 'Dignity Statements · Normalizing',
      subtitle: '"That happens more than people think."',
      items: [
        '"That happens more than people think."',
        '"Plenty of people end up in that spot."',
        '"That reaction makes sense given what was going on."',
        'When to use — When they think what they did or felt makes them broken or alone.',
      ],
    },
    {
      title: 'Dignity Statements · Reflecting Understanding',
      subtitle: '"I hear you. That was hard."',
      items: [
        '"I hear you."',
        '"That was hard."',
        '"Thanks for telling me that straight."',
        '"I get why that happened."',
        'When to use — When they need to know the story landed before you say anything else.',
      ],
    },
    {
      title: 'Dignity Statements · Removing Shame',
      subtitle: '"You did what you could with what you had."',
      items: [
        '"You had a lot going on."',
        '"You did what you could with what you had at the time."',
        '"Given the circumstances, that tracks."',
        'When to use — When they\'re holding shame that\'s making them small in front of you.',
      ],
    },
    {
      title: 'Dignity Statements · Non-Judgmental',
      subtitle: '"No judgment here."',
      items: [
        '"I\'m not judging that."',
        '"No judgment here."',
        '"That\'s just what happened. Nothing more to make of it."',
        'When to use — When your face or role could easily be read as verdict-in-waiting.',
      ],
    },
    {
      title: 'Dignity Statements · Encouraging',
      subtitle: '"What matters now is what you do next."',
      items: [
        '"Cool that you didn\'t hold back with me."',
        '"I appreciate you being straight with me about that."',
        '"What matters now is what you do next."',
        'When to use — When the story has landed and the room needs a forward motion, not a rehash.',
      ],
    },

    // ── GATE 5 NEXT STEPS ──
    {
      title: 'Gate 5 — Next Steps',
      subtitle: 'What happens from here?',
      items: [
        'You don\'t need agreement. You need clarity.',
        'Both people leave knowing what\'s next — or knowing that nothing has changed.',
        'Either is a result.',
      ],
    },
    {
      title: 'Next Steps — the four options',
      items: [
        'If you act — Name the commitment. What you\'ll do, by when, and what that changes.',
        'If you don\'t act — Name that too. Both people need to leave knowing where things stand.',
        'If there\'s no resolution — Say so clearly. False closure is worse than open ends.',
        'Even clarity counts — Sometimes the only next step is that both people know the truth. That\'s enough.',
        'When it breaks — All four earlier gates opened. No one named what comes next. Nothing moved.',
        'Carry — Name it. Whatever it is.',
      ],
    },
  ],
};

// ────────────────────────────────────────────────────────────────────────
// DECK 5 — OPERATION LISTEN (full detail)
// ────────────────────────────────────────────────────────────────────────
const D_OL = {
  id: 'operation-listen',
  title: 'Operation Listen',
  tagline: 'Live drill · 4 roles · 7 rungs · Threat Meter',
  color: '#C45A3B',
  slides: [
    {
      title: 'Operation Listen',
      subtitle: 'Listening under pressure',
      items: [
        'A live, real-time drill for 2–4 players.',
        'One 4-digit room code. Everyone joins on the same code.',
        'One round is one hard conversation.',
        'The Listening Chain and its seven rungs get tested against a live opponent.',
      ],
    },
    {
      title: 'How it runs — the flow',
      items: [
        'Facilitator opens the room and shares the 4-digit code.',
        'Coach and Guide are prompted to run The Reset before the round starts.',
        'Conflict Coach sets the Threat Meter. Conflict player picks a scenario and a stance.',
        'The round runs. Coach taps rungs as they hear them. Guide leads the conversation.',
        'When all 7 rungs are heard, Guide gets the Paraphrase banner. Guide reflects it back and taps Paraphrase.',
        'Round ends. Debrief. Rotate roles.',
      ],
    },

    // ── ROLES DEEP DIVE ──
    {
      title: 'The Four Roles',
      subtitle: 'Each one owns a piece of the room',
      items: [
        'Coach — the ear.',
        'Guide — the mouth.',
        'Conflict — the person bringing the hard thing.',
        'Conflict Coach — the pressure dial.',
      ],
    },
    {
      title: 'Role · Coach',
      subtitle: 'They can be heard. Window is clear.',
      items: [
        'Watches the conversation from the outside.',
        'Taps each rung on the ladder as they hear it land in the Conflict player\'s speech.',
        'Can send up to 2 nudges to the Guide — short prompts like "Ask about Proof" or "Reflect the feeling."',
        'Nudge buttons only unlock when Guide is on Meter view (no ladder visible) — the nudge replaces the ladder.',
        'Coach\'s nudges also count towards the Paraphrase Meter.',
      ],
    },
    {
      title: 'Role · Guide',
      subtitle: 'Leads the conversation. Waits for the signal.',
      items: [
        'Talks to the Conflict player. Runs the Listening Chain live.',
        'Sees either the Ladder view (7 rungs visible) or Meter-only view (rungs hidden, only the paraphrase meter).',
        'On Meter-only, has to feel the round without seeing what\'s been heard — a harder mode.',
        'When all 7 rungs are heard, the big Paraphrase Now banner appears.',
        'Guide reflects it back in their own words and taps Paraphrase — banner turns forest-green: Paraphrased.',
      ],
    },
    {
      title: 'Role · Conflict',
      subtitle: 'The person bringing the hard thing',
      items: [
        'Draws a rung — the level of intensity for their opening.',
        'Picks a scenario — a hard conversation from the app\'s scenario deck.',
        'Picks a stance — Own or Blame. That shapes how they enter the room.',
        'Their job is to be real, not to win. They bring the hard thing honestly.',
      ],
    },
    {
      title: 'Role · Conflict Coach',
      subtitle: 'The pressure dial',
      items: [
        'Owns the Threat Meter.',
        'Escalates when techniques aren\'t landing — when the Guide is missing rungs or steamrolling.',
        'De-escalates when the Guide lands a real move — a genuine reflection, a well-placed rung.',
        'The point isn\'t to win. It\'s to let the Guide land moves.',
      ],
    },

    // ── SEVEN RUNGS IN OL ──
    {
      title: 'The Empathy Ladder — in the live drill',
      subtitle: 'The Empathy Ladder gate, under pressure',
      items: [
        '1. I Feel _______',
        '2. One Off, or?',
        '3. Old / New?',
        '4. On Purpose?',
        '5. Who\'s Fault?',
        '6. Proof?',
        '7. Ask / Demand?',
        'Coach taps them as they hear them. All 7 heard → Guide gets Paraphrase Now.',
      ],
    },

    // ── VIEW MODES ──
    {
      title: 'Ladder vs Meter — the Guide chooses',
      items: [
        'Ladder view — Guide sees the 7 rungs and can watch the Coach light them up in real time.',
        'Meter-only view — Guide sees just the Paraphrase Meter (0–7). Rungs are hidden.',
        'On Meter-only, Coach\'s nudge buttons unlock — that\'s the way to point without the ladder visible.',
        'Ladder view is the easier mode. Meter-only is the pressure mode.',
      ],
    },

    // ── THREAT METER ──
    {
      title: 'The Threat Meter',
      subtitle: 'Stable → Escalating → High → Full Conflict',
      items: [
        'Four states, in order: Stable · Escalating · High · Full Conflict.',
        'Owned by the Conflict Coach.',
        'Escalates when techniques aren\'t landing.',
        'De-escalates when the Guide lands a real move.',
        'Full Conflict doesn\'t mean the round is over — it means the moves have to get sharper.',
      ],
    },

    // ── PARAPHRASE ──
    {
      title: 'The Paraphrase',
      subtitle: 'Close the loop',
      items: [
        'When all 7 rungs are heard, Guide gets a big Paraphrase Now banner.',
        'Reflect it back in your own words — not the exact wording, but the substance.',
        'Tap Paraphrase → the moment gets logged. Banner turns forest-green: Paraphrased.',
        'Then invite the Conflict player to speak from what they own — the pivot to Respect and Next Steps.',
      ],
    },

    // ── FACILITATOR MODE ──
    {
      title: 'The Facilitator Screen',
      subtitle: 'Single-screen, 4-panel view',
      items: [
        'One facilitator can run the whole drill from one screen.',
        'The screen shows all 4 role panels side-by-side: Coach, Guide, Conflict, Conflict Coach.',
        'Useful for workshops where players share a screen or the facilitator narrates the state.',
        'Reset first? Y/N — Coach and Guide are prompted to run The Reset before they enter.',
      ],
    },
    {
      title: 'Reset first? Y/N',
      subtitle: 'The gate before the room',
      items: [
        'Before Coach or Guide enters their panel, a Reset gate appears.',
        'Yes → runs the 90-second Reset from The Body hub.',
        'No → skips straight in.',
        'The point isn\'t compliance — it\'s the pause. Even choosing "No" is a decision, not a reflex.',
      ],
    },
  ],
};

// ────────────────────────────────────────────────────────────────────────
// DECK 6 — AAA CHECKLIST (full detail — every gate, both paths)
// ────────────────────────────────────────────────────────────────────────
const D_AAA = {
  id: 'aaa',
  title: 'AAA Checklist',
  tagline: 'Agency · Autonomy · Accountability — Self & Other',
  color: '#C45A3B',
  slides: [
    {
      title: 'AAA Checklist',
      subtitle: 'Three gates. Two paths each.',
      items: [
        'Agency — I can act.',
        'Autonomy — I can choose.',
        'Accountability — I am of consequence.',
        'Each gate has a Self path (about you) and an Other path (about someone else).',
        'Run this quietly before you speak.',
      ],
    },

    // AGENCY
    {
      title: 'Agency',
      subtitle: 'I can act.',
      items: [
        'Before choice, before consequences — the question is whether action is available at all.',
        'Agency is not willpower. It\'s raw physical and mental capacity.',
        'Body present, mind reachable, environment permitting.',
      ],
    },
    {
      title: 'Agency · Self — the five checks',
      subtitle: 'Can I act?',
      items: [
        'Is my body physically okay right now?',
        'Am I clearheaded — not intoxicated, not in crisis?',
        'Is the environment safe enough for me to act in?',
        'Am I being prevented from acting by something outside me?',
        'If I tried to move — to do something — could I?',
      ],
    },
    {
      title: 'Agency · Self — what blocks it',
      items: [
        'Physical injury or pain — "I\'m hurt." Real limit on what the body can do.',
        'Intoxication — "I\'m not fully here." Action taken here may not reflect the decision you\'d actually make.',
        'Overwhelm or shutdown — "I can\'t move." The body is okay but nothing is happening.',
        'External constraint — "Something is blocking me." The environment may be restricting action.',
        'Acute distress or grief — "I\'m not okay right now." Agency partially offline — not permanently.',
        'Carry — If the body can\'t move and the mind can\'t hold, nothing else matters yet.',
      ],
    },
    {
      title: 'Agency · Other — the five checks',
      subtitle: 'Can they act?',
      items: [
        'Does this person seem physically present and okay?',
        'Are they clearheaded — or is something impairing them?',
        'Is the environment safe enough for them to act in?',
        'Is something or someone preventing them from acting freely?',
        'Could they actually do something right now if they chose to?',
      ],
    },
    {
      title: 'Agency · Other — what blocks it',
      items: [
        'Visible injury or physical limit — Pain limits what they can engage with. Reduce the ask.',
        'Intoxication — Not fully here. What they agree to may not hold. Don\'t use this moment.',
        'Shutdown or freeze — Quiet, but not by choice. Reduce pressure first. Presence returns.',
        'External constraint — Something is cutting off their options. Help them locate what is available.',
        'Grief, shock, or acute distress — Don\'t bring the material. Be with the person.',
        'Carry — You can\'t reach someone who can\'t act. Agency first, then everything else.',
      ],
    },

    // AUTONOMY
    {
      title: 'Autonomy',
      subtitle: 'I can choose.',
      items: [
        'Agency says you can act. Autonomy says you get to decide what action to take — or whether to act at all.',
        'Choice is what gives action meaning.',
        'Without it, movement is just reaction.',
      ],
    },
    {
      title: 'Autonomy · Self — the five checks',
      subtitle: 'Is the choice mine?',
      items: [
        'Do I see at least two real options in front of me?',
        'Is this choice actually mine — or am I being pushed?',
        'Can I choose to do nothing? Is inaction a real option?',
        'Am I choosing from fear — or from what I actually want?',
        'Do I understand what I\'m choosing between?',
      ],
    },
    {
      title: 'Autonomy · Self — what blocks it',
      items: [
        'Coercion or pressure — "You have to." What\'s left isn\'t choice, it\'s compliance.',
        'False binary — "It\'s either this or that." Often more options exist, just not visible yet.',
        'Fear-driven deciding — Choosing to avoid pain isn\'t the same as choosing what you want.',
        'Information gap — Deciding without clarity is guessing with consequences.',
        'Habitual reaction — What feels like a choice may be the default. The groove can be so deep it doesn\'t feel like one.',
        'Carry — Choice requires options you can actually see — and a decision that belongs to you.',
      ],
    },
    {
      title: 'Autonomy · Other — the five checks',
      subtitle: 'Do they have a real choice?',
      items: [
        'Does this person see more than one real option?',
        'Are they being coerced — openly or subtly?',
        'Do they know they can choose inaction?',
        'Do they understand what they\'re actually choosing between?',
        'Is the decision coming from them — or from the pressure they\'re under?',
      ],
    },
    {
      title: 'Autonomy · Other — what blocks it',
      items: [
        'Coercion — visible or subtle — Pressure from rank, fear, or expectation can make compliance look like agreement.',
        'False binary — They see two bad options and think that\'s all there is.',
        'Fear driving the decision — They\'re choosing whatever feels safest, not what they want.',
        'Missing information — Gap in understanding leads to a decision they may not stand behind.',
        'Pattern, not choice — They\'re about to do what they always do.',
        'Carry — Your job is to open the field, not to fill it. Their choice. Not yours.',
      ],
    },

    // ACCOUNTABILITY
    {
      title: 'Accountability',
      subtitle: 'I am of consequence.',
      items: [
        'Accountability isn\'t blame and it isn\'t punishment.',
        'It\'s the recognition that what you do — and what you don\'t do — matters.',
        'To someone. To something. Including yourself.',
        'This is where agency and autonomy become responsibility.',
      ],
    },
    {
      title: 'Accountability · Self — the five checks',
      subtitle: 'What did I set in motion?',
      items: [
        'What did my action — or inaction — actually cause?',
        'Who is affected by what I did or didn\'t do?',
        'Am I willing to own this — not just acknowledge it?',
        'What does owning it actually require of me?',
        'What would it look like to be accountable here — not just sorry?',
      ],
    },
    {
      title: 'Accountability · Self — what blocks it',
      items: [
        'Blame redirect — "They made me do it." Separate what others did from what you did. Both can be true.',
        'Guilt without action — Feeling bad isn\'t the same as taking responsibility.',
        'Not looking at the impact — Intent and impact are two different things.',
        'Proportion distortion — "It\'s all my fault." / "It had nothing to do with me." Both block ownership.',
        'Accountability without agency — Being blamed for something you couldn\'t change isn\'t accountability. It\'s punishment.',
        'Carry — Agency got you here. Autonomy gave you the choice. This is where it lands.',
      ],
    },
    {
      title: 'Accountability · Action → Consequence',
      items: [
        'Act — What was set in motion? Who\'s affected? What does owning it require?',
        'Don\'t act — Inaction has consequences too. What did not acting allow to continue?',
        'Own it — Trust. Repair. The relationship can move forward.',
        'Avoid it — The situation stays exactly where it is. Or gets heavier.',
      ],
    },
    {
      title: 'Accountability · Other — coaching someone through it',
      subtitle: 'Can they own this?',
      items: [
        'Accountability only arrives when someone believes they could have acted and that the choice was theirs.',
        'Without that foundation, what you\'re offering isn\'t accountability — it\'s blame.',
        'Your job is to hold the space, not deliver the verdict.',
        'Check: do they believe they had agency? Do they believe the choice was theirs?',
        'If either is missing, the accountability conversation can\'t land yet.',
      ],
    },
  ],
};

// ────────────────────────────────────────────────────────────────────────
// DECK 7 — THE STORY WE TELL
// ────────────────────────────────────────────────────────────────────────
const D_STORY = {
  id: 'the-story',
  title: 'The Story',
  tagline: 'The Story We Tell · 90-day journaling arc',
  color: '#C45A3B',
  slides: [
    {
      title: 'The Story We Tell',
      subtitle: 'Third hub — offline journaling, timed by the app',
      items: [
        'The app times a reflection window.',
        'The prompt is on-screen. You write in a physical journal.',
        'Screen goes dark when the time is up. No typing in the app.',
      ],
    },
    {
      title: 'Why offline',
      items: [
        'Screens tempt shortcuts. Handwriting slows the pace to the pace of the thought.',
        'The journal becomes the record — one you can flip back through.',
        'The app\'s job is timing, sequencing, and holding you to the ritual.',
      ],
    },
    {
      title: 'The 90-day arc',
      items: [
        'A prompt a day, sequenced across a season.',
        'The prompts excavate how tone shaped your life — and rebuild the narrative from the parts you own.',
        'The through-line: what did you inherit vs what did you choose?',
      ],
    },
    {
      title: 'How a session runs',
      items: [
        'Open The Story. Today\'s prompt appears.',
        'The app starts the timer — you write, longhand, in your notebook.',
        'When the timer ends, the screen dims. That\'s the signal to stop.',
        'You mark it complete in the app. The prompt for tomorrow queues up.',
        'Miss a day? The next day\'s prompt still comes. You don\'t backfill — you continue.',
      ],
    },
  ],
};

// ────────────────────────────────────────────────────────────────────────
// DECK 8 — THE FLASHCARDS (nudges between drills)
// ────────────────────────────────────────────────────────────────────────
const D_FLASH = {
  id: 'flashcards',
  title: 'Flashcards',
  tagline: 'The nudge deck — 19 cards that point back to drills',
  color: '#C45A3B',
  slides: [
    {
      title: 'Flashcards',
      subtitle: 'A nudge system, not a lesson',
      items: [
        'A rotating deck of 19 short cards.',
        'Each card is a truth on the front, and a drill suggestion on the back.',
        'They\'re how the app nudges you back into practice when you\'re just opening it out of habit.',
      ],
    },
    {
      title: 'Cards · Body & Eyes',
      items: [
        '"Where your eyes go, your ears follow." → Eye Anchor.',
        '"Fast scanning reads as anxiety. A still gaze reads as steady." → Eye Anchor.',
        '"Your hands mirror your tone." → Hand Grip Dial.',
        '"When it\'s time to be firm, plant your feet. Literally." → Root & Rise.',
        '"Better to get and give a full body yes or no." → Still Head, Moving Mind.',
      ],
    },
    {
      title: 'Cards · Face & Chin',
      items: [
        '"Relax the face. Let your thinking guide your lips." → Face Dial.',
        '"Chin position broadcasts authority, equality, or uncertainty." → Chin Check.',
      ],
    },
    {
      title: 'Cards · Voice & Tone',
      items: [
        '"Communication is a subtle game of high and low." → Status Dial.',
        '"As we learn to listen beneath people\'s words, keep it down." → Listen Softer.',
        '"Your voice reveals how you\'re holding the conversation." → Good Food–Good Chat.',
        '"There are times to be direct. There are times to be roundabout." → The Conductor\'s Arms.',
      ],
    },
    {
      title: 'Cards · Mind & Approach',
      items: [
        '"Listen without suspicion." → One More Thing.',
        '"Do I really mean that? Do they really mean that?" → Reappraise Pause.',
        '"A run-it-by-me and a walk-me-through-it need different pacing." → Match the pacing to the moment.',
        '"Dignity is the first thing at risk in conflict." → Keep tone and body language on the side of respect.',
      ],
    },
    {
      title: 'Cards · Habit & Voice-to-self',
      items: [
        '"A daily reset is preventative maintenance." → Right-side weight shift or a big yawn-stretch.',
        '"The spine shapes the boundaries you hold." → Daily Posture Audit.',
        '"Guarding your time is guarding your power." → Postpone with dignity.',
        '"Speak to others the way you\'d speak to yourself, at your best." → Notice which voice — harsh, timid, or grounded — you default to.',
      ],
    },
  ],
};

export const DECKS = [
  D_BODY,
  D_BODY_DRILLS,
  D_VOICE,
  D_LISTENING_CHAIN,
  D_OL,
  D_AAA,
  D_STORY,
  D_FLASH,
];
